import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faExternalLinkAlt,
  faUniversity,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import {
  createConnectAccount,
  createConnectAccountLink,
  getConnectAccountStatus,
} from "../../utils/bookings";
import { useToast } from "../ui/Toast";
import "../../styles/PaymentsManager.css";

/**
 * PaymentsManager — Stripe Connect Express onboarding UI for a business
 * owner. Lives inside the BusinessDashboard "Payments" tab.
 *
 * Flow:
 *   1. Owner clicks "Connect your bank" → callable `createConnectAccount`.
 *   2. We immediately call `createConnectAccountLink` and open the
 *      Stripe-hosted onboarding URL in a new tab.
 *   3. On return, owner clicks "Refresh status" → callable
 *      `getConnectAccountStatus` which syncs from Stripe and updates
 *      `connectAccounts/{uid}` in Firestore.
 */
export default function PaymentsManager() {
  const { showToast } = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const s = await getConnectAccountStatus();
      setStatus(s);
    } catch (err) {
      showToast(err?.message || "Couldn't load Connect status.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleCreate = async () => {
    setBusy(true);
    try {
      await createConnectAccount();
      const link = await createConnectAccountLink();
      window.open(link.url, "_blank", "noopener");
      showToast("Opened Stripe onboarding in a new tab.", "info");
    } catch (err) {
      showToast(err?.message || "Couldn't start Connect onboarding.", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = async () => {
    setBusy(true);
    try {
      const link = await createConnectAccountLink();
      window.open(link.url, "_blank", "noopener");
    } catch (err) {
      showToast(err?.message || "Couldn't open Stripe onboarding.", "error");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="payments-manager__loading">
        <FontAwesomeIcon icon={faSpinner} spin /> Loading payment status…
      </div>
    );
  }

  const exists = status && status.exists;
  const ready = exists && status.chargesEnabled && status.payoutsEnabled;
  const incomplete = exists && !ready;

  return (
    <div className="payments-manager">
      <h2>
        <FontAwesomeIcon icon={faUniversity} /> Payments &amp; Payouts
      </h2>
      <p className="payments-manager__lead">
        Connect a bank account through Stripe to receive booking and event
        ticket revenue directly. We only charge a commission when the Guild
        helps generate a confirmed booking or paid registration.
      </p>

      {!exists && (
        <div className="payments-manager__cta">
          <p>You haven't connected a payout method yet.</p>
          <button type="button" className="btn-primary" onClick={handleCreate} disabled={busy}>
            {busy ? <><FontAwesomeIcon icon={faSpinner} spin /> Starting…</> : "Connect your bank"}
          </button>
        </div>
      )}

      {incomplete && (
        <div className="payments-manager__cta payments-manager__cta--warn">
          <p>
            <FontAwesomeIcon icon={faExclamationTriangle} /> Your Stripe onboarding is incomplete.
            {status.requirementsDue && status.requirementsDue.length > 0 && (
              <> Stripe needs: <code>{status.requirementsDue.join(", ")}</code>.</>
            )}
          </p>
          <div className="payments-manager__actions">
            <button type="button" className="btn-primary" onClick={handleContinue} disabled={busy}>
              <FontAwesomeIcon icon={faExternalLinkAlt} /> Continue onboarding
            </button>
            <button type="button" className="btn-secondary" onClick={refresh} disabled={busy}>
              <FontAwesomeIcon icon={faSync} /> Refresh status
            </button>
          </div>
        </div>
      )}

      {ready && (
        <div className="payments-manager__cta payments-manager__cta--ok">
          <p>
            <FontAwesomeIcon icon={faCheckCircle} /> Your Stripe Connect account is ready to accept payments.
          </p>
          <div className="payments-manager__actions">
            <button type="button" className="btn-secondary" onClick={handleContinue} disabled={busy}>
              <FontAwesomeIcon icon={faExternalLinkAlt} /> Manage in Stripe
            </button>
            <button type="button" className="btn-secondary" onClick={refresh} disabled={busy}>
              <FontAwesomeIcon icon={faSync} /> Refresh status
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
